import { supabase } from '../lib/supabase';

/**
 * Client Portal Service
 * Handles all client-facing data operations for the self-service portal
 */

export const clientPortalService = {
  /**
   * Get client's owned properties with current values
   */
  async getClientProperties(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('contracts')?.select(`id,contract_number,status,start_date,end_date,amount,property:properties(id,property_id,title,property_type,purpose,status,bedrooms,bathrooms,area,address,city,district,price,currency,images)`)?.eq('client_id', clientId)?.in('status', ['active', 'approved'])
        ?.order('start_date', { ascending: false });

      if (error) throw error;

      return data?.map(contract => ({
        contractId: contract?.id,
        contractNumber: contract?.contract_number,
        contractStatus: contract?.status,
        startDate: contract?.start_date,
        endDate: contract?.end_date,
        contractAmount: contract?.amount,
        property: {
          id: contract?.property?.id,
          propertyId: contract?.property?.property_id,
          title: contract?.property?.title,
          type: contract?.property?.property_type,
          purpose: contract?.property?.purpose,
          status: contract?.property?.status,
          bedrooms: contract?.property?.bedrooms,
          bathrooms: contract?.property?.bathrooms,
          area: contract?.property?.area,
          address: contract?.property?.address,
          city: contract?.property?.city,
          district: contract?.property?.district,
          price: contract?.property?.price,
          currency: contract?.property?.currency,
          images: contract?.property?.images || []
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching client properties:', error);
      throw new Error(error?.message || 'فشل في جلب العقارات');
    }
  },

  /**
   * Get client's pending transactions and contracts
   */
  async getPendingTransactions(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('contracts')
        ?.select(`
          id,
          contract_number,
          contract_type,
          status,
          start_date,
          end_date,
          amount,
          created_at,
          property:properties(
            title,
            property_type
          )
        `)
        ?.eq('client_id', clientId)
        ?.in('status', ['pending', 'approved'])
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(contract => ({
        id: contract?.id,
        contractNumber: contract?.contract_number,
        contractType: contract?.contract_type,
        status: contract?.status,
        startDate: contract?.start_date,
        endDate: contract?.end_date,
        amount: contract?.amount,
        propertyTitle: contract?.property?.title,
        propertyType: contract?.property?.property_type,
        createdAt: contract?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching pending transactions:', error);
      throw new Error(error?.message || 'فشل في جلب المعاملات المعلقة');
    }
  },

  /**
   * Get contract deadlines with countdown
   */
  async getContractDeadlines(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('contracts')
        ?.select(`
          id,
          contract_number,
          contract_type,
          end_date,
          status,
          property:properties(title)
        `)
        ?.eq('client_id', clientId)
        ?.eq('status', 'active')
        ?.not('end_date', 'is', null)
        ?.order('end_date', { ascending: true });

      if (error) throw error;

      const now = new Date();
      return data?.map(contract => {
        const endDate = new Date(contract?.end_date);
        const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        
        return {
          id: contract?.id,
          contractNumber: contract?.contract_number,
          contractType: contract?.contract_type,
          endDate: contract?.end_date,
          propertyTitle: contract?.property?.title,
          daysRemaining,
          isExpiringSoon: daysRemaining <= 30 && daysRemaining > 0,
          isExpired: daysRemaining < 0
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching contract deadlines:', error);
      throw new Error(error?.message || 'فشل في جلب مواعيد العقود');
    }
  },

  /**
   * Get payment schedule and history
   */
  async getPaymentSchedule(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('contracts')
        ?.select(`
          id,
          contract_number,
          amount,
          start_date,
          end_date,
          property:properties(title, currency)
        `)
        ?.eq('client_id', clientId)
        ?.in('status', ['active', 'approved'])
        ?.order('start_date', { ascending: false });

      if (error) throw error;

      return data?.map(contract => ({
        contractId: contract?.id,
        contractNumber: contract?.contract_number,
        propertyTitle: contract?.property?.title,
        totalAmount: contract?.amount,
        currency: contract?.property?.currency || 'JOD',
        startDate: contract?.start_date,
        endDate: contract?.end_date,
        paymentStatus: 'paid' // Mock status - would come from payment records
      })) || [];
    } catch (error) {
      console.error('Error fetching payment schedule:', error);
      throw new Error(error?.message || 'فشل في جلب جدول الدفعات');
    }
  },

  /**
   * Get client's documents and contracts
   */
  async getClientDocuments(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('contracts')
        ?.select(`
          id,
          contract_number,
          contract_type,
          documents,
          created_at,
          status,
          property:properties(title)
        `)
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(contract => ({
        id: contract?.id,
        contractNumber: contract?.contract_number,
        contractType: contract?.contract_type,
        propertyTitle: contract?.property?.title,
        documents: contract?.documents || [],
        createdAt: contract?.created_at,
        status: contract?.status
      })) || [];
    } catch (error) {
      console.error('Error fetching client documents:', error);
      throw new Error(error?.message || 'فشل في جلب المستندات');
    }
  },

  /**
   * Get client's contracts including buyer representation contracts
   */
  async getClientContracts(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('contracts')
        ?.select(`
          id,
          contract_number,
          contract_type,
          status,
          start_date,
          end_date,
          amount,
          created_at,
          property:properties(
            title,
            property_type,
            address
          ),
          buyer_representation:buyer_representation_contracts(
            id,
            arddjo_retainer_percentage,
            client_retainer_percentage,
            arddjo_retainer_amount,
            client_retainer_amount,
            representation_duration_months,
            exclusive_representation,
            property_types,
            budget_min,
            budget_max,
            preferred_locations,
            arddjo_acceptance_status,
            client_acceptance_status,
            arddjo_accepted_at,
            client_accepted_at
          )
        `)
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(contract => ({
        id: contract?.id,
        contractNumber: contract?.contract_number,
        contractType: contract?.contract_type,
        status: contract?.status,
        startDate: contract?.start_date,
        endDate: contract?.end_date,
        amount: contract?.amount,
        createdAt: contract?.created_at,
        property: contract?.property,
        buyerRepresentation: contract?.buyer_representation?.[0] || null
      })) || [];
    } catch (error) {
      console.error('Error fetching client contracts:', error);
      throw new Error(error?.message || 'فشل في جلب العقود');
    }
  },

  /**
   * Get communication history
   */
  async getCommunicationHistory(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('communications')
        ?.select(`
          id,
          channel,
          message,
          sent_at,
          status,
          user:user_profiles(full_name)
        `)
        ?.eq('client_id', clientId)
        ?.order('sent_at', { ascending: false })
        ?.limit(20);

      if (error) throw error;

      return data?.map(comm => ({
        id: comm?.id,
        channel: comm?.channel,
        message: comm?.message,
        sentAt: comm?.sent_at,
        status: comm?.status,
        consultantName: comm?.user?.full_name || 'مستشار عقاري'
      })) || [];
    } catch (error) {
      console.error('Error fetching communication history:', error);
      throw new Error(error?.message || 'فشل في جلب سجل التواصل');
    }
  },

  /**
   * Get upcoming appointments
   */
  async getUpcomingAppointments(clientId) {
    try {
      const now = new Date()?.toISOString();
      
      const { data, error } = await supabase
        ?.from('appointments')
        ?.select(`
          id,
          title,
          description,
          appointment_date,
          duration,
          location,
          status,
          property:properties(title)
        `)
        ?.eq('client_id', clientId)
        ?.gte('appointment_date', now)
        ?.in('status', ['scheduled', 'rescheduled'])
        ?.order('appointment_date', { ascending: true })
        ?.limit(10);

      if (error) throw error;

      return data?.map(apt => ({
        id: apt?.id,
        title: apt?.title,
        description: apt?.description,
        appointmentDate: apt?.appointment_date,
        duration: apt?.duration,
        location: apt?.location,
        status: apt?.status,
        propertyTitle: apt?.property?.title
      })) || [];
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw new Error(error?.message || 'فشل في جلب المواعيد');
    }
  },

  /**
   * Get client profile information
   */
  async getClientProfile(userId) {
    try {
      const { data, error } = await supabase
        ?.from('clients')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        email: data?.email,
        phone: data?.phone,
        category: data?.category,
        status: data?.status,
        preferredLocation: data?.preferred_location,
        budgetMin: data?.budget_min,
        budgetMax: data?.budget_max,
        propertyType: data?.property_type,
        notes: data?.notes
      };
    } catch (error) {
      console.error('Error fetching client profile:', error);
      throw new Error(error?.message || 'فشل في جلب معلومات العميل');
    }
  },

  /**
   * Get signature requests for client
   */
  async getClientSignatureRequests(clientId) {
    try {
      // Get client's user_id first
      const { data: clientData, error: clientError } = await supabase
        ?.from('clients')
        ?.select('user_id')
        ?.eq('id', clientId)
        ?.single();

      if (clientError) throw clientError;

      const { data, error } = await supabase
        ?.from('signature_requests')
        ?.select(`
          id,
          status,
          requested_at,
          due_date,
          signer_type,
          contract:contracts(
            contract_number,
            contract_type
          )
        `)
        ?.eq('signer_id', clientData?.user_id)
        ?.eq('signer_type', 'client')
        ?.order('requested_at', { ascending: false });

      if (error) throw error;

      return data?.map(req => ({
        id: req?.id,
        status: req?.status,
        requestedAt: req?.requested_at,
        dueDate: req?.due_date,
        contractNumber: req?.contract?.contract_number,
        contractType: req?.contract?.contract_type
      })) || [];
    } catch (error) {
      console.error('Error fetching signature requests:', error);
      throw new Error(error?.message || 'فشل في جلب طلبات التوقيع');
    }
  }
};

export default clientPortalService;